import * as z from "zod";
import Workspace, { WorkspaceFolder } from "@/models/workspace/Workspace";

/**
 * The API's limit on a whole document name, base name plus extension. Mirrored here so
 * an over-long name fails in the form instead of on the request.
 */
export const MAX_DOCUMENT_NAME_LENGTH = 255;

/**
 * Characters a file name may not contain — the set OneDrive, SharePoint and Windows all
 * refuse. Mirrors `WorkspaceDocumentService.ForbiddenNameCharacters`; a cloud-linked
 * file's new name is pushed to OneDrive/SharePoint, where any of these fails late.
 */
export const DOCUMENT_NAME_FORBIDDEN_CHARACTERS = '"*:<>?/\\|';

export const DOCUMENT_NAME_EMPTY_MESSAGE = "Udfyld navn";
export const DOCUMENT_NAME_FORBIDDEN_MESSAGE =
  'Navn må ikke indeholde " * : < > ? / \\ |';
export const DOCUMENT_NAME_TAKEN_MESSAGE =
  "Der findes allerede en fil med dette navn";

/**
 * A document name split at its extension. `extension` keeps its leading dot, or is empty
 * when the name has none.
 *
 * The rename form only edits `baseName`: a document's name is also its download file
 * name, and `DocumentType` was decided from the bytes at upload — so dropping or changing
 * the extension would hand the user a file their machine no longer knows how to open,
 * without changing what the file actually is.
 *
 * A leading dot is part of the base name, not an extension ("`.gitignore`" is a name, not
 * an extension), so only a dot with something before it splits.
 */
export const splitDocumentName = (
  name: string
): { baseName: string; extension: string } => {
  const lastDotIndex = name.lastIndexOf(".");
  if (lastDotIndex <= 0) return { baseName: name, extension: "" };

  return {
    baseName: name.substring(0, lastDotIndex),
    extension: name.substring(lastDotIndex),
  };
};

/**
 * The full name to send for a document renamed to `baseName`, carrying over the extension
 * of `currentName`. Surrounding whitespace on the base name is dropped, so the extension
 * always sits directly against it.
 */
export const buildDocumentName = (
  currentName: string,
  baseName: string
): string => `${baseName.trim()}${splitDocumentName(currentName).extension}`;

/**
 * Names of the documents that sit next to `documentId` — the other documents in its
 * folder, or the other root documents when it sits at the root. The document itself is
 * excluded, so renaming it to its current name is not reported as a clash.
 *
 * The API refuses a rename that collides with a sibling's name, as OneDrive and SharePoint
 * do, so the rule is mirrored here to fail in the form instead of on the request.
 */
export const getSiblingDocumentNames = (
  workspace: Workspace | null,
  documentId: string | undefined
): string[] => {
  if (!workspace || !documentId) return [];

  const holds = (folder: { documents: { id: string }[] }) =>
    folder.documents.some((d) => d.id === documentId);

  const findHolder = (
    folders: WorkspaceFolder[]
  ): { documents: { id: string; name: string }[] } | null => {
    for (const folder of folders) {
      if (holds(folder)) return folder;
      const nested = findHolder(folder.folders);
      if (nested) return nested;
    }
    return null;
  };

  const holder = holds(workspace) ? workspace : findHolder(workspace.folders);
  if (!holder) return [];

  return holder.documents.filter((d) => d.id !== documentId).map((d) => d.name);
};

/** Trim + lowercase: the folding the API applies when it compares names. */
const fold = (name: string): string => name.trim().toLowerCase();

/**
 * Schema for the rename form's single field, the base name. `extension` is the suffix that
 * will be appended, so the length and sibling rules apply to the whole name the API will
 * see. `siblingNames` must exclude the document being renamed (see
 * {@link getSiblingDocumentNames}).
 */
export const buildRenameDocumentSchema = (
  extension: string,
  siblingNames: string[] = []
) => {
  const maxBaseNameLength = Math.max(
    0,
    MAX_DOCUMENT_NAME_LENGTH - extension.length
  );
  const taken = new Set(siblingNames.map(fold));
  const forbidden = new RegExp(
    `[${DOCUMENT_NAME_FORBIDDEN_CHARACTERS.replace(/[\\\]^-]/g, "\\$&")}]`
  );

  return z.object({
    baseName: z
      .string()
      .refine((value) => value.trim().length > 0, {
        message: DOCUMENT_NAME_EMPTY_MESSAGE,
      })
      .refine((value) => value.trim().length <= maxBaseNameLength, {
        message: `Navn må højst være ${maxBaseNameLength} tegn`,
      })
      .refine((value) => !forbidden.test(value), {
        message: DOCUMENT_NAME_FORBIDDEN_MESSAGE,
      })
      .refine((value) => !taken.has(fold(`${value.trim()}${extension}`)), {
        message: DOCUMENT_NAME_TAKEN_MESSAGE,
      }),
  });
};

export type RenameDocumentFormValues = z.infer<
  ReturnType<typeof buildRenameDocumentSchema>
>;
